<template>
  <div class="space-y-6">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <div class="text-sm font-semibold text-slate-700">Staff</div>
        <h2 class="mt-1 text-2xl font-bold tracking-tight text-slate-900">Gate operators</h2>
      </div>
      <button class="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500" @click="openCreate">
        + Add operator
      </button>
    </div>

    <div class="rounded-2xl bg-white p-4 ring-1 ring-slate-200">
      <div class="overflow-x-auto rounded-xl ring-1 ring-slate-200">
        <table class="w-full min-w-[700px] text-left text-xs">
          <thead class="bg-slate-50 text-slate-600">
            <tr>
              <th class="px-3 py-2 font-semibold">Name</th>
              <th class="px-3 py-2 font-semibold">Username</th>
              <th class="px-3 py-2 font-semibold">Role</th>
              <th class="px-3 py-2 font-semibold">Status</th>
              <th class="px-3 py-2 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in operators" :key="u.id" class="border-t border-slate-200">
              <td class="px-3 py-2 font-semibold text-slate-900">{{ u.name }}</td>
              <td class="px-3 py-2 font-mono text-slate-700">{{ u.username }}</td>
              <td class="px-3 py-2">
                <span class="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700 ring-1 ring-sky-200">{{ u.role }}</span>
              </td>
              <td class="px-3 py-2">
                <span
                  class="rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1"
                  :class="u.active ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-slate-100 text-slate-700 ring-slate-200'"
                >
                  {{ u.active ? "active" : "inactive" }}
                </span>
              </td>
              <td class="px-3 py-2 text-right">
                <button class="rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50" @click="openEdit(u)">
                  Edit
                </button>
                <button
                  class="ml-2 rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  @click="toggle(u)"
                >
                  {{ u.active ? "Deactivate" : "Activate" }}
                </button>
              </td>
            </tr>
            <tr v-if="operators.length === 0">
              <td class="px-3 py-8 text-center text-slate-500" colspan="5">No operators yet.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- <div class="mt-3 text-xs text-slate-500">
        Default operator login: <span class="font-mono text-slate-700">operator / operator123</span>
      </div> -->
    </div>

    <div v-if="modalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div class="w-full max-w-lg rounded-2xl bg-white p-5 shadow-lg ring-1 ring-slate-200">
        <div class="flex items-start justify-between">
          <div>
            <div class="text-sm font-semibold text-slate-700">{{ editingId ? "Edit" : "Create" }}</div>
            <div class="mt-1 text-xl font-bold text-slate-900">Operator account</div>
          </div>
          <button class="rounded-lg px-2 py-1 text-sm text-slate-600 hover:bg-slate-50" @click="close">✕</button>
        </div>

        <form class="mt-4 grid gap-4 sm:grid-cols-2" @submit.prevent="save">
          <div class="sm:col-span-2">
            <label class="text-xs font-semibold text-slate-700">Full name</label>
            <input v-model.trim="form.name" class="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none ring-sky-200 focus:ring-4" />
          </div>
          <div>
            <label class="text-xs font-semibold text-slate-700">Username</label>
            <input v-model.trim="form.username" class="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono outline-none ring-sky-200 focus:ring-4" />
          </div>
          <div>
            <label class="text-xs font-semibold text-slate-700">Password</label>
            <input v-model="form.password" type="text" class="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono outline-none ring-sky-200 focus:ring-4" />
          </div>

          <div class="sm:col-span-2 flex items-center justify-end gap-2 pt-2">
            <button type="button" class="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" @click="close">
              Cancel
            </button>
            <button type="submit" class="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import { toast } from "@/toast";

const store = useStore();
const modalOpen = ref(false);
const editingId = ref("");

const operators = computed(() =>
  store.state.staffUsers.filter((u) => u.role === "operator")
);

const form = reactive({
  name: "",
  username: "",
  password: "",
});

function openCreate() {
  editingId.value = "";
  form.name = "";
  form.username = "";
  form.password = "";
  modalOpen.value = true;
}

function openEdit(u) {
  editingId.value = u.id;
  form.name = u.name || "";
  form.username = u.username || "";
  form.password = u.password || "";
  modalOpen.value = true;
}

function close() {
  modalOpen.value = false;
}

function save() {
  if (!form.name.trim()) return toast.error("Name is required.");
  if (!form.username.trim()) return toast.error("Username is required.");
  if (!form.password.trim()) return toast.error("Password is required.");

  store.commit("upsertStaff", {
    id: editingId.value || undefined,
    name: form.name,
    username: form.username,
    password: form.password,
    role: "operator",
    active: true,
  });
  store.commit("log", {
    action: editingId.value ? "update_operator" : "create_operator",
    actorId: store.getters.authedUser?.id,
    actorRole: "admin",
    message: `Operator saved: ${form.username}`,
  });
  toast.success("Saved.");
  close();
}

function toggle(u) {
  store.commit("setStaffActive", { id: u.id, active: !u.active });
  store.commit("log", {
    action: "toggle_operator_status",
    actorId: store.getters.authedUser?.id,
    actorRole: "admin",
    message: `Operator ${u.username} set to ${!u.active ? "inactive" : "active"}.`,
  });
  toast.info(u.active ? "Deactivated." : "Activated.");
}
</script>

